/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ActionIcon,
  ActionIcon$inboundSchema,
  ActionIcon$Outbound,
  ActionIcon$outboundSchema,
} from "./actionicon.js";
import {
  Actions,
  Actions$inboundSchema,
  Actions$Outbound,
  Actions$outboundSchema,
} from "./actions.js";
import {
  DropdownComponentConfig,
  DropdownComponentConfig$inboundSchema,
  DropdownComponentConfig$Outbound,
  DropdownComponentConfig$outboundSchema,
} from "./dropdowncomponentconfig.js";

/**
 * The variant of the confirm button.
 */
export const ConfirmVariant = {
  Default: "default",
  Tier2: "tier2",
  Tier3: "tier3",
  Brand: "brand",
  Destructive: "destructive",
  Inverse: "inverse",
  Link: "link",
  Transparent: "transparent",
} as const;
/**
 * The variant of the confirm button.
 */
export type ConfirmVariant = OpenEnum<typeof ConfirmVariant>;

/**
 * The config of the confirmation dialog.
 */
export type ConfirmationDialogConfig = {
  /**
   * The configuration for a dropdown.
   */
  dropdownComponentConfig?: DropdownComponentConfig | undefined;
  /**
   * Actions to take
   */
  actions?: Actions | undefined;
  /**
   * The label of the cancel button.
   */
  cancelLabel: string;
  /**
   * The label of the confirm button.
   */
  confirmLabel: string;
  /**
   * The variant of the confirm button.
   */
  confirmVariant?: ConfirmVariant | undefined;
  /**
   * The description of the confirmation dialog.
   */
  description?: string | undefined;
  /**
   * The config of the action icon.
   */
  icon?: ActionIcon | undefined;
  /**
   * The title of the confirmation dialog.
   */
  title: string;
};

/** @internal */
export const ConfirmVariant$inboundSchema: z.ZodType<
  ConfirmVariant,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(ConfirmVariant);
/** @internal */
export const ConfirmVariant$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  ConfirmVariant
> = openEnums.outboundSchema(ConfirmVariant);

/** @internal */
export const ConfirmationDialogConfig$inboundSchema: z.ZodType<
  ConfirmationDialogConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  DropdownComponentConfig: types.optional(
    DropdownComponentConfig$inboundSchema,
  ),
  actions: types.optional(Actions$inboundSchema),
  cancel_label: types.string(),
  confirm_label: types.string(),
  confirm_variant: ConfirmVariant$inboundSchema.default("default"),
  description: types.optional(types.string()),
  icon: types.optional(ActionIcon$inboundSchema),
  title: types.string(),
}).transform((v) => {
  return remap$(v, {
    "DropdownComponentConfig": "dropdownComponentConfig",
    "cancel_label": "cancelLabel",
    "confirm_label": "confirmLabel",
    "confirm_variant": "confirmVariant",
  });
});
/** @internal */
export type ConfirmationDialogConfig$Outbound = {
  DropdownComponentConfig?: DropdownComponentConfig$Outbound | undefined;
  actions?: Actions$Outbound | undefined;
  cancel_label: string;
  confirm_label: string;
  confirm_variant: string;
  description?: string | undefined;
  icon?: ActionIcon$Outbound | undefined;
  title: string;
};

/** @internal */
export const ConfirmationDialogConfig$outboundSchema: z.ZodType<
  ConfirmationDialogConfig$Outbound,
  z.ZodTypeDef,
  ConfirmationDialogConfig
> = z.object({
  dropdownComponentConfig: DropdownComponentConfig$outboundSchema.optional(),
  actions: Actions$outboundSchema.optional(),
  cancelLabel: z.string(),
  confirmLabel: z.string(),
  confirmVariant: ConfirmVariant$outboundSchema.default("default"),
  description: z.string().optional(),
  icon: ActionIcon$outboundSchema.optional(),
  title: z.string(),
}).transform((v) => {
  return remap$(v, {
    dropdownComponentConfig: "DropdownComponentConfig",
    cancelLabel: "cancel_label",
    confirmLabel: "confirm_label",
    confirmVariant: "confirm_variant",
  });
});

export function confirmationDialogConfigToJSON(
  confirmationDialogConfig: ConfirmationDialogConfig,
): string {
  return JSON.stringify(
    ConfirmationDialogConfig$outboundSchema.parse(confirmationDialogConfig),
  );
}
export function confirmationDialogConfigFromJSON(
  jsonString: string,
): SafeParseResult<ConfirmationDialogConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ConfirmationDialogConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ConfirmationDialogConfig' from JSON`,
  );
}
